import React, { useCallback, useMemo } from 'react';
import { translate } from '@utils/i18n';
import { FilterBar } from '@ui/organisms/FilterBar';
import { IssueTypeEnum, IssueStatusEnum } from '../../types';
import { humanizeInspectableType } from '../../utils/humanizeInspectableType';
import { EMPTY_FILTERS } from './types';
const NS = 'features.iim_review.issues_grid';
const CARD_NS = 'features.iim_review.issue_card';
const FILTER_OPTIONS_NS = `${NS}.filter_options`;
function getIssueTypeLabel(type) {
    return translate(type, { namespace: `${NS}.issue_type_labels`, defaultValue: type });
}
function getStatusLabel(status) {
    return translate(status, { namespace: `${CARD_NS}.status`, defaultValue: status });
}
export default function IssuesGridFilterBar({ filters, onFiltersChange, filterOptions, searchValue, onSearchChange, onResetSelection }) {
    const issueTypeOptions = useMemo(() => ((filterOptions === null || filterOptions === void 0 ? void 0 : filterOptions.issueTypes) || Object.values(IssueTypeEnum)).map((type) => ({
        label: getIssueTypeLabel(type),
        value: type
    })), [filterOptions === null || filterOptions === void 0 ? void 0 : filterOptions.issueTypes]);
    const statusOptions = useMemo(() => Object.values(IssueStatusEnum).map((status) => ({
        label: getStatusLabel(status),
        value: status
    })), []);
    const deviceTypeOptions = useMemo(() => ((filterOptions === null || filterOptions === void 0 ? void 0 : filterOptions.inspectableTypes) || []).map((type) => ({
        label: humanizeInspectableType(type),
        value: type
    })), [filterOptions === null || filterOptions === void 0 ? void 0 : filterOptions.inspectableTypes]);
    const impactOptions = useMemo(() => [
        { label: translate('high', { namespace: `${NS}.impact` }), value: 'high' },
        { label: translate('low', { namespace: `${NS}.impact` }), value: 'low' }
    ], []);
    const filterConfigs = useMemo(() => [
        {
            type: 'multiSelect',
            field: 'issueType',
            label: translate('issue_type', { namespace: FILTER_OPTIONS_NS }),
            value: filters.issueTypes,
            options: issueTypeOptions,
            onChange: (v) => onFiltersChange(Object.assign(Object.assign({}, filters), { issueTypes: v }))
        },
        {
            type: 'multiSelect',
            field: 'status',
            label: translate('issue_status', { namespace: FILTER_OPTIONS_NS }),
            value: filters.statuses,
            options: statusOptions,
            onChange: (v) => onFiltersChange(Object.assign(Object.assign({}, filters), { statuses: v }))
        },
        {
            type: 'multiSelect',
            field: 'deviceType',
            label: translate('device_type', { namespace: FILTER_OPTIONS_NS }),
            value: filters.inspectableTypes,
            options: deviceTypeOptions,
            onChange: (v) => onFiltersChange(Object.assign(Object.assign({}, filters), { inspectableTypes: v }))
        },
        {
            type: 'select',
            field: 'impact',
            label: translate('impact', { namespace: FILTER_OPTIONS_NS }),
            value: filters.impact,
            options: impactOptions,
            onChange: (v) => onFiltersChange(Object.assign(Object.assign({}, filters), { impact: v }))
        }
    ], [filters, onFiltersChange, issueTypeOptions, statusOptions, deviceTypeOptions, impactOptions]);
    const handleResetFilters = useCallback(() => {
        onFiltersChange(EMPTY_FILTERS);
        onSearchChange('');
        onResetSelection === null || onResetSelection === void 0 ? void 0 : onResetSelection();
    }, [onFiltersChange, onSearchChange, onResetSelection]);
    return (React.createElement("div", { className: "issues-grid-filters" },
        React.createElement(FilterBar, { filters: filterConfigs, onResetFilters: handleResetFilters, toggleDisabled: true, showSearch: true, searchValue: searchValue, onSearchChange: onSearchChange, searchPlaceholder: translate('search_placeholder', { namespace: NS }) })));
}
